import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  ArrowLeft, CheckCircle, XCircle, Loader2, Star, Camera, User, Calendar,
  MapPin, FileText, AlertTriangle, Sparkles
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ScoreMeter = ({ score, label, color = "orange" }) => {
  const colorClasses = {
    orange: "text-[#F56F05]",
    green: "text-green-500",
    red: "text-red-500",
    gray: "text-zinc-400"
  };
  
  return (
    <div className="text-center">
      <div className={`text-5xl font-bold ${colorClasses[color]}`}>{Math.round(score)}</div>
      <div className="text-sm text-zinc-500 mt-1">{label}</div>
    </div>
  );
};

const CategoryBreakdown = ({ breakdown, title }) => {
  if (!breakdown) return null;
  
  return (
    <div className="space-y-2">
      <p className="font-medium text-zinc-700 text-sm">{title}</p>
      {Object.entries(breakdown).map(([key, data]) => (
        <div key={key} className="flex items-center justify-between text-sm">
          <span className="text-zinc-600">{data.label}</span>
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1,2,3,4,5].map((n) => (
                <Star
                  key={n}
                  className={`h-4 w-4 ${n <= data.rating ? 'text-[#F56F05] fill-[#F56F05]' : 'text-zinc-200'}`}
                />
              ))}
            </div>
            <span className="text-zinc-400 w-8 text-right">{data.normalized_score}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const AdminReportReviewPage = () => {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/reports');
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [appointment, setAppointment] = useState(null);
  const [report, setReport] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [apptRes, reportRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments/${appointmentId}`, { headers }),
        axios.get(`${API_URL}/api/appointments/${appointmentId}/cleaner-report`, { headers })
      ]);
      
      setAppointment(apptRes.data);
      setReport(reportRes.data);
      
      try {
        const assessRes = await axios.get(`${API_URL}/api/appointments/${appointmentId}/condition-assessment`, { headers });
        setAssessment(assessRes.data);
      } catch (_) {
        // No assessment exists
      }
    } catch (_) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [appointmentId, headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/appointments/${appointmentId}/report-review/approve`, {}, { headers });
      toast.success('Report approved and released');
      
      if (res.data.condition_decline) {
        toast.warning('Condition decline detected! Client will see restoration recommendation.');
      }
      
      navigate('/admin/reports');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleRequestFix = async () => {
    if (!rejectNotes.trim()) {
      toast.error('Please provide notes for the cleaner');
      return;
    }
    
    setProcessing(true);
    try {
      await axios.post(`${API_URL}/api/appointments/${appointmentId}/report-review/request-fix`, null, {
        headers,
        params: { notes: rejectNotes }
      });
      toast.success('Fix requested');
      setShowRejectDialog(false);
      navigate('/admin/reports');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request fix');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={back}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl font-bold text-zinc-900">Report Review</h1>
          </div>
        </header>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-zinc-300 mb-4" />
              <p className="text-zinc-500">No report found for this appointment</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const improvementDelta = report.improvement_delta;
  const showDeclineWarning = assessment && improvementDelta && improvementDelta < -15;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={back} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">QC Review</h1>
              <p className="text-sm text-zinc-500">{appointment?.service?.name} • {appointment?.client_name}</p>
            </div>
          </div>
          <Badge className={
            report.status === 'pending_qc' ? 'bg-amber-500' :
            report.status === 'released' ? 'bg-green-500' :
            report.status === 'fix_requested' ? 'bg-red-500' : 'bg-zinc-500'
          }>
            {report.status === 'pending_qc' ? 'Pending Review' :
             report.status === 'released' ? 'Released' :
             report.status === 'fix_requested' ? 'Fix Requested' : report.status}
          </Badge>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Score Summary */}
        <Card>
          <CardContent className="py-8">
            <div className="grid grid-cols-3 gap-8 text-center">
              {assessment && (
                <ScoreMeter score={assessment.before_score} label="BEFORE" color="gray" />
              )}
              <ScoreMeter score={report.after_score} label="AFTER" color="orange" />
              {improvementDelta !== null && (
                <div className="text-center">
                  <div className={`text-5xl font-bold ${improvementDelta >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {improvementDelta >= 0 ? '+' : ''}{Math.round(improvementDelta)}
                  </div>
                  <div className="text-sm text-zinc-500 mt-1">IMPROVEMENT</div>
                  {improvementDelta >= 15 && (
                    <Badge className="mt-2 bg-green-500">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Significant Restoration
                    </Badge>
                  )}
                </div>
              )}
            </div>
            
            {showDeclineWarning && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3"
              >
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Potential Condition Decline</p>
                  <p className="text-sm text-red-700 mt-1">
                    Score dropped by {Math.abs(improvementDelta)} points. If approved, client will see a restoration recommendation.
                  </p>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>

        {/* Side by Side Comparison */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Before */}
          {assessment && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Before Assessment</CardTitle>
                <CardDescription>
                  by {assessment.performed_by_name} • {new Date(assessment.created_at).toLocaleDateString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <CategoryBreakdown breakdown={assessment.breakdown_json} title="Ratings" />
                
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {assessment.before_overview_photo_url && (
                    <img
                      src={assessment.before_overview_photo_url}
                      alt="Before Overview"
                      className="rounded-lg w-full h-32 object-cover"
                    />
                  )}
                  {assessment.before_highlight_photo_url && (
                    <img
                      src={assessment.before_highlight_photo_url}
                      alt="Before Highlight"
                      className="rounded-lg w-full h-32 object-cover"
                    />
                  )}
                </div>
                
                {assessment.notes && (
                  <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-600">
                    <p className="font-medium text-zinc-700 mb-1">Notes:</p>
                    {assessment.notes}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* After */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">After Report</CardTitle>
              <CardDescription>
                by {appointment?.cleaner_name} • {new Date(report.submitted_at).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <CategoryBreakdown breakdown={report.breakdown_json} title="Ratings" />
              
              <div className="grid grid-cols-2 gap-2 mt-4">
                {report.after_overview_photo_url && (
                  <img
                    src={report.after_overview_photo_url}
                    alt="After Overview"
                    className="rounded-lg w-full h-32 object-cover"
                  />
                )}
                {report.after_highlight_photo_url && (
                  <img
                    src={report.after_highlight_photo_url}
                    alt="After Highlight"
                    className="rounded-lg w-full h-32 object-cover"
                  />
                )}
              </div>
              
              {report.notes && (
                <div className="bg-zinc-50 rounded-lg p-3 text-sm text-zinc-600">
                  <p className="font-medium text-zinc-700 mb-1">Notes:</p>
                  {report.notes}
                </div>
              )}
              
              {report.latitude && report.longitude && (
                <div className="flex items-center gap-2 text-sm text-zinc-500">
                  <MapPin className="h-4 w-4" />
                  GPS: {report.latitude.toFixed(4)}, {report.longitude.toFixed(4)}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        {report.status === 'pending_qc' && (
          <Card>
            <CardContent className="py-6">
              <div className="flex gap-4">
                <Button
                  onClick={handleApprove}
                  disabled={processing}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  size="lg"
                  data-testid="approve-btn"
                >
                  {processing ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-5 w-5 mr-2" />
                  )}
                  Approve & Release
                </Button>
                <Button
                  onClick={() => setShowRejectDialog(true)}
                  disabled={processing}
                  variant="destructive"
                  className="flex-1"
                  size="lg"
                  data-testid="request-fix-btn"
                >
                  <XCircle className="h-5 w-5 mr-2" />
                  Request Fix
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Already Released */}
        {report.status === 'released' && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="py-6 text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-3" />
              <p className="font-semibold text-green-800">Report Approved & Released</p>
              <p className="text-sm text-green-700 mt-1">
                QC verified on {report.qc_verified_at ? new Date(report.qc_verified_at).toLocaleString() : 'N/A'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Request Fix Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Fix from Cleaner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Explain what needs to be fixed or improved..."
              rows={4}
              data-testid="reject-notes"
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowRejectDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={handleRequestFix}
                disabled={processing}
                variant="destructive"
                className="flex-1"
                data-testid="confirm-request-fix"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Send Request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminReportReviewPage;
